import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileText, Briefcase, Receipt, Ruler, DollarSign, Image as ImageIcon } from "lucide-react";
import { Link } from "react-router-dom";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");

/**
 * Property history dialog — surfaces prior estimates, jobs, invoices,
 * saved measurements, prior service pricing, and photos for a given
 * property (address). Tenant-scoped read-only view.
 */
export default function PropertyHistoryDialog({ open, onClose, propertyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !propertyId) return;
    setLoading(true); setData(null);
    api.get(`/estimator/properties/${propertyId}/history`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [open, propertyId]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="property-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Property history</DialogTitle>
        </DialogHeader>

        {loading && <div className="text-sm text-zinc-500 py-6 text-center">Loading history…</div>}
        {data?.error && <div className="text-sm text-red-600 py-6 text-center">Failed to load property history.</div>}

        {data && !data.error && (
          <div className="space-y-6">
            <PropertyHead property={data.property} />

            <Section title="Saved measurements" count={data.measurements.length} icon={Ruler} testid="history-measurements">
              {data.measurements.length === 0 && <Empty text="No measurements yet."/>}
              <div className="grid md:grid-cols-2 gap-2">
                {data.measurements.map((m) => (
                  <Row key={m.id} testid={`history-measurement-${m.id}`}>
                    <div className="text-sm font-medium">{m.label || m.calculator_key}</div>
                    <div className="text-xs font-mono-accent text-zinc-600">
                      {Number(m.output_value || 0).toLocaleString()} {m.output_unit || m.input_unit || ""}
                    </div>
                    <div className="text-[10px] text-zinc-400">{fmtDate(m.created_at)}</div>
                  </Row>
                ))}
              </div>
            </Section>

            <Section title="Estimates" count={data.estimates.length} icon={FileText} testid="history-estimates">
              {data.estimates.length === 0 && <Empty text="No estimates yet."/>}
              <div className="space-y-2">
                {data.estimates.map((e) => (
                  <Row key={e.id} testid={`history-estimate-${e.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="text-sm font-medium truncate">#{e.number || "—"} · {e.title || "Estimate"}</div>
                      <StatusPill status={e.status} />
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                      <span className="text-xs text-zinc-500">{fmtDate(e.created_at)}</span>
                      <span className="font-mono-accent text-sm">{money(e.total)}</span>
                      <Link to={`/app/estimates/${e.id}`} className="text-xs text-[#FF5A00] hover:underline">Open</Link>
                    </div>
                  </Row>
                ))}
              </div>
            </Section>

            <Section title="Jobs" count={data.jobs.length} icon={Briefcase} testid="history-jobs">
              {data.jobs.length === 0 && <Empty text="No jobs yet."/>}
              <div className="space-y-2">
                {data.jobs.map((j) => (
                  <Row key={j.id} testid={`history-job-${j.id}`}>
                    <div className="text-sm font-medium truncate">{j.title || "Job"}</div>
                    <StatusPill status={j.status} />
                    <div className="ml-auto text-xs text-zinc-500">{fmtDate(j.completed_at || j.scheduled_at || j.created_at)}</div>
                  </Row>
                ))}
              </div>
            </Section>

            <Section title="Invoices" count={data.invoices.length} icon={Receipt} testid="history-invoices">
              {data.invoices.length === 0 && <Empty text="No invoices yet."/>}
              <div className="space-y-2">
                {data.invoices.map((i) => (
                  <Row key={i.id} testid={`history-invoice-${i.id}`}>
                    <div className="text-sm font-medium truncate">#{i.number || "—"}</div>
                    <StatusPill status={i.status} />
                    <div className="ml-auto flex items-center gap-3">
                      <span className="text-xs text-zinc-500">{fmtDate(i.created_at)}</span>
                      <span className="font-mono-accent text-sm">{money(i.total)}</span>
                      {i.balance > 0 && <span className="text-xs text-red-600">bal {money(i.balance)}</span>}
                    </div>
                  </Row>
                ))}
              </div>
            </Section>

            <Section title="Prior pricing (per service)" count={data.prior_pricing.length} icon={DollarSign} testid="history-pricing">
              {data.prior_pricing.length === 0 && <Empty text="No prior pricing recorded."/>}
              <div className="space-y-1">
                {data.prior_pricing.map((p, i) => (
                  <Row key={i} testid={`history-pricing-${i}`}>
                    <div className="text-sm truncate">{p.service_name}</div>
                    <div className="ml-auto flex items-center gap-3">
                      <span className="text-xs text-zinc-500">#{p.estimate_number}</span>
                      <span className="text-xs text-zinc-500">qty {Number(p.quantity || 0).toLocaleString()}</span>
                      <span className="font-mono-accent text-sm">{money(p.unit_price)}</span>
                    </div>
                  </Row>
                ))}
              </div>
            </Section>

            {data.photos.length > 0 && (
              <Section title="Photos" count={data.photos.length} icon={ImageIcon} testid="history-photos">
                <div className="grid grid-cols-4 gap-2">
                  {data.photos.map((id) => (
                    <div key={id} className="aspect-square bg-zinc-100 rounded flex items-center justify-center text-xs text-zinc-500">
                      <ImageIcon className="w-5 h-5"/>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PropertyHead({ property }) {
  if (!property) return null;
  return (
    <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50">
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono-accent">Property</div>
      <div className="font-heading text-lg font-bold">{property.label || "Property"}</div>
      {property.address && <div className="text-sm text-zinc-600">{property.address}</div>}
    </div>
  );
}

function Section({ title, count, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#FF5A00]" />
        <div className="font-heading font-bold">{title}</div>
        <span className="ml-auto text-xs text-zinc-500 font-mono-accent">{count}</span>
      </div>
      {children}
    </div>
  );
}

function Row({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-zinc-200 rounded-lg px-3 py-2 flex items-center gap-2"
    >
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-xs text-zinc-500 py-2">{text}</div>;
}

function StatusPill({ status }) {
  if (!status) return null;
  const cls = {
    draft: "bg-zinc-100 text-zinc-700",
    sent: "bg-blue-50 text-blue-700",
    accepted: "bg-emerald-50 text-emerald-700",
    declined: "bg-red-50 text-red-700",
    completed: "bg-emerald-50 text-emerald-700",
    scheduled: "bg-blue-50 text-blue-700",
    in_progress: "bg-amber-50 text-amber-700",
    paid: "bg-emerald-50 text-emerald-700",
    unpaid: "bg-amber-50 text-amber-700",
    overdue: "bg-red-50 text-red-700",
  }[status] || "bg-zinc-100 text-zinc-700";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-widest ${cls}`}>{status}</span>;
}
